import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { LEGACY_DATASETS_TOOLTIP } from 'webapp/enums/metric-mover';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isVerified } from 'webapp/utils/verification';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BillingLimitationWarningComponent from 'webapp/components/billing/limitation-warning';
import CommonRequestTrialLinkComponent from 'webapp/components/common/request-trial-link';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanLinkComponent from 'webapp/components/common/upgrade-plan-link';
import MetricLibraryMetricActionsBaseComponent from 'webapp/components/metric-library/metric/actions-base';
import MetricLibraryMetricInfoComponent from 'webapp/components/metric-library/metric/info';
import { MetricTabId } from 'webapp/components/metric-library/popups/popup-metric-extra-info';
import MetricAuditInfoComponent from 'webapp/components/metric/audit-info';
import ToolTipComponent from 'webapp/components/tool-tip';

import MetricSetup from 'webapp/models-custom/metric-setup';
import type PopupContext from 'webapp/models-custom/popup/context';
import type MetricsAddBlockTour from 'webapp/models-custom/tour/metrics-add-block';

import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

interface Args {
	popupModel: PopupContext;
}

export default class MetricLibraryPopupsMetric extends MetricLibraryMetricActionsBaseComponent<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('tour') declare tourService: Service.Tour;
	@service('connect') declare connectService: Service.Connect;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('popup') declare popupService: Service.Popup;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;

	@tracked bubbleTour: MetricsAddBlockTour | null = null;

	origin: TrackingOriginForMetricEdit = 'allmetrics';

	get popupData() {
		return this.args.popupModel.data as PopupData.MetricLibrary.MetricInfo;
	}

	get metric() {
		return this.popupData.model;
	}

	get relatedModel() {
		const metric = this.metric;
		const objectId = metric.objectId;
		if (!objectId) {
			return null;
		}
		if (metric.isTypeCustomQuery) {
			return this.mmCustomQueriesService.findModel(String(objectId));
		}
		if (metric.isTypeMetricCalculation) {
			return this.mmScriptsService.findModel(String(objectId));
		}
		return null;
	}

	get auditCreatedByName(): string | null {
		return this.relatedModel?.createdBy?.name ?? null;
	}

	get auditCreatedTimestamp(): number | null {
		return this.relatedModel?.created ?? null;
	}

	get auditModifiedByName(): string | null {
		return this.relatedModel?.modifiedBy?.name ?? null;
	}

	get auditModifiedTimestamp(): number | null {
		return this.relatedModel?.model?.modified ?? null;
	}

	get showDragNDrop() {
		return this.popupData.showDragNDrop;
	}

	get showActions() {
		return this.popupData.showActions;
	}

	get goalsEnabled() {
		return this.billingService.goalsEnabled;
	}

	get scriptsEnabled() {
		return this.billingService.scriptsEnabled;
	}

	get qbEnabled() {
		return this.billingService.qbEnabled;
	}

	get dataPushEnabled() {
		return this.billingService.dataPushEnabled;
	}

	get isRequestTrialLinkShown() {
		return this.billingService.canRequestTrial || !!this.payingTrialPromotions.length;
	}

	get payingTrialPromotions() {
		const targetPlan = this.billingService.getPlanForTrial(BillingFeature.QB),
			payingTrialPromotions = targetPlan
				? this.flowPromotionsService.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(
						targetPlan.planType,
					)
				: [];

		return payingTrialPromotions;
	}

	get sourceType() {
		return this.metric.sourceType;
	}

	get source() {
		return this.metric.source;
	}

	get showSourceError() {
		return !!this.source?.isStatusActionable && !this.currentUserService.isDemo;
	}

	get showConnect() {
		return !this.source?.id && !this.currentUserService.isDemo;
	}

	get showCreateFromPredefined() {
		return this.metric.mustResolveBeforeSelecting;
	}

	get showDeleteForGenericCustomMetric() {
		return this.metric.isUserGeneric;
	}

	get showWarning() {
		return this.metric.showQbWarning || this.metric.showScriptWarning;
	}

	get showQbLink() {
		return this.sourceType.isQBSupported && !this.metric.isTypeMetricCalculationOrGeneric;
	}

	get hasActions() {
		return !this.showSourceError && !this.showConnect && !this.showWarning && this.showActions;
	}

	get showDivider() {
		return (this.metric.description || this.showActions) && !this.showWarning;
	}

	get isFavorite() {
		return this.metric.isFavorite;
	}

	get isVerified() {
		return isVerified(this.metric.verificationInfo);
	}

	get canEditMetricDefinition() {
		// check editMetricWillOpenQb to avoid showing 2 buttons for "Edit Built metric"
		return this.metric.canEditDefinition && !this.metric.editMetricWillOpenQb;
	}

	get trackingOrigin() {
		return this.popupData.trackingOrigin || this.origin;
	}

	@action
	connect() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		this.popupData.onConnect();
		this.close();
	}

	@action
	createFromPredefined() {
		const spaceAccessId = this.metric.spaceAccessId;
		if (!spaceAccessId) {
			return;
		}

		const infolayer = this.infolayerService.loading('Creating metric...'),
			metric = this.metric;
		void this.mmMetricsService
			.resolveGenericMetricWithSpecific(metric.key, spaceAccessId)
			.then((metricData) => {
				if (metricData) {
					if (metric.isTypeCustomQueryOrGeneric) {
						this.trackCreateFromGenericMetric(TrackingEvent.QUERY_CREATED);
					} else if (metric.isTypeMetricCalculationOrGeneric) {
						this.trackCreateFromGenericMetric(TrackingEvent.CALCULATION_CREATED);
					}

					infolayer.ok('Metric created!');
				} else {
					infolayer.close();
				}
			});

		this.close();
	}

	@action
	helpConnect() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (this.currentUserService.model?.isVerified) {
			this.sourceType.requestConnect();
		} else {
			this.connectService.openEmailVerificationInfoPopup();
		}
	}

	@action
	createBlockAndClose() {
		this.createBlock();
		if (this.popupData.onCreateBlock) {
			this.popupData.onCreateBlock();
		}
		this.close();
	}

	@action
	addToHomeAndClose() {
		this.close();
		void this.mmMetricsService.addMetricsToHome([this.metric]);
	}

	@action
	addToFavoritesAndClose() {
		this.close();
		void this.addToFavorites();
	}

	@action
	removeFromFavoritesAndClose() {
		this.close();
		void this.removeFromFavorites();
	}

	@action
	createGoalAndClose() {
		this.createGoal();
		this.close();
	}

	@action
	contactForTrial() {
		this.close();

		later(() => {
			void this.billingService.requestTrial(this.metric.missingBillingFeature);
		}, 500);
	}

	@action
	editQueryAndClose() {
		this.close();
		this.editQuery();
	}

	@action
	editScriptAndClose() {
		this.close();
		this.editScript();
	}

	@action
	duplicateMetricAndClose() {
		this.close();
		this.duplicateMetric();
	}

	@action
	pushDataAndClose() {
		this.close();
		this.pushData();
	}

	@action
	purgeDataAndClose() {
		this.close();
		this.purgeData();
	}

	@action
	openVerificationAndClose() {
		void this.mmMetricsService.toggleVerification(this.metric);
		this.close();
	}

	@action
	deleteMetricAndClose() {
		this.close();
		this.deleteMetric();
	}

	@action
	viewDimensions() {
		this.mmMetricsService.openExtraInfoPopup(this.metric, MetricTabId.DIMENSIONS);
	}

	@action
	solveDatasource() {
		if (this.source?.isBillingLink) {
			this.navigationService.transitionToRoute('settings.billing');
		} else if (this.source?.canReconnect || this.source?.canReauthorize) {
			void this.connectService.connectSourceFromIdOrKey(this.source.type, {
				sourceId: this.source.numericId,
				reauthorize: this.source.canReauthorize,
				origin: this.origin,
			});
			this.close();
		}
	}

	@action
	openMetricInsightsDrawer() {
		const metricData = this.metric.metricData!;
		const metricSetup = new MetricSetup(metricData);
		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup,
			},
			this.origin,
		);
	}

	@action
	deleteGenericMetric() {
		const affirmativeFunc = () => {
			void this.metric.deleteAndCatch();
		};
		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Do you want to delete the "${this.metric.name}" metric?`,
			text: "If this metric is used on any dashboards or other features, it'll be reset to empty. This is not reversible.",
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	@action
	setupView() {
		if (this.showDragNDrop) {
			return;
		}

		if (!this.hasActions) {
			return;
		}

		if (this.tourService.isEligibleForMetricsAddBlockTour) {
			later(() => {
				const tour = this.tourService.initTour(TourId.METRICS_ADD_BLOCK);
				if (tour) {
					tour.start();
				}
				this.bubbleTour = tour;
			}, 1000);
		}
	}

	@action
	teardownView() {
		const tour = this.bubbleTour;
		if (tour) {
			tour.end();
			this.bubbleTour = null;
		}
	}

	@action
	close() {
		void this.args.popupModel.close();
	}

	private trackCreateFromGenericMetric(trackingEvent: TrackingEvent) {
		const eventName = trackingEvent,
			eventData: APIData.Tracking.Data = {
				origin: this.trackingOrigin,
				datasource: this.source?.sourceType.key,
			};

		this.analyticsService.event(eventName, eventData);
	}
	<template>
		<div {{didInsert this.setupView}} {{willDestroy this.teardownView}}></div>

		<MetricLibraryMetricInfoComponent
			@model={{this.metric}}
			@onOpenExtraInfoPopup={{this.close}}
		/>

		<MetricAuditInfoComponent
			@verification={{this.metric.verificationInfo}}
			@createdByName={{this.auditCreatedByName}}
			@createdTimestamp={{this.auditCreatedTimestamp}}
			@modifiedByName={{this.auditModifiedByName}}
			@modifiedTimestamp={{this.auditModifiedTimestamp}}
		/>

		{{#if this.showDivider}}
			<div class="divider"></div>
		{{/if}}

		{{#if this.showSourceError}}

			<div class="error">

				{{#if this.source.isStatusActionable}}
					{{#if this.source.canResolveActionableStatus}}
						<a
							role="button"
							class="button size-small"
							{{on "click" this.solveDatasource}}
						>
							<CommonUiIconComponent
								@name="Connect"
								@size="sm"
							/>{{this.source.statusActionCapitalized}}
						</a>
					{{else}}
						<LinkTo class="button size-small" @route="datamanager">View in Data manager</LinkTo>
					{{/if}}
				{{/if}}

				<span class="text">
					<CommonUiIconComponent @name="AlertFill" @size="sm" />
					Data source in error
					{{#if this.source.statusObject.statusTitleAndDescription}}
						<ToolTipComponent
							@text={{this.source.statusObject.statusTitleAndDescription}}
							@interactive={{true}}
						/>
					{{/if}}
				</span>

			</div>

		{{else if this.showWarning}}

			<div class="warning">
				<CommonUiIconComponent @name="Lock" @size="sm" />
				{{#if this.metric.showQbWarning}}
					This is a built metric made with
					<LinkTo @route="metrics">Metric builder</LinkTo>, that is not available with
					your current plan.
				{{else if this.metric.showScriptWarning}}
					This is a calculated metric made with
					<LinkTo @route="metrics">Data calculations</LinkTo>, that are not available with
					your current plan.
				{{/if}}
				{{#if this.isRequestTrialLinkShown}}
					<CommonRequestTrialLinkComponent
						@feature={{this.metric.missingBillingFeature}}
						@trackingOrigin="metric_library_tile"
						@onTrigger={{this.close}}
					/>
				{{else}}
					<CommonUpgradePlanLinkComponent
						@text="Explore the plan"
						@feature={{this.metric.missingBillingFeature}}
						@trackingOrigin="metric_library_tile"
					/>
				{{/if}}
			</div>

		{{else if this.showActions}}

			{{#if this.showConnect}}

				<div class="connect">
					<a role="button" class="button theme-green" {{on "click" this.connect}}>
						<CommonUiIconComponent @name="Connect" @size="sm" />Connect
					</a>
					<a role="button" class="support" {{on "click" this.helpConnect}}>
						Ask teammate to connect
					</a>
				</div>

				{{#if this.showDeleteForGenericCustomMetric}}
					<div class="delete-generic">
						<a role="button" {{on "click" this.deleteGenericMetric}}>Delete metric</a>
					</div>
				{{/if}}

			{{else if this.showDragNDrop}}

				<div class="action draggable">
					<img src="/images/arrow-drag-green.png" height="9" alt />
					Drag and drop this datablock to the canvas
				</div>

			{{else if this.showCreateFromPredefined}}

				<div class="connect">
					<a role="button" class="button" {{on "click" this.createFromPredefined}}>
						<span>Create</span>
					</a>
				</div>

			{{else}}

				<div class="actions-ct divider">

					{{#if this.currentUserService.hasHome}}
						<a role="button" {{on "click" this.addToHomeAndClose}} class="blue">
							<CommonUiIconComponent @name="Plus" @size="sm" />
							Add to Home
						</a>
					{{/if}}

					<a role="button" {{on "click" this.createBlockAndClose}} class="blue">
						<CommonUiIconComponent @name="Databoards" @size="sm" />
						Add to dashboard
					</a>

					{{#if this.mmMetricsService.showFavoriteAction}}
						{{#if this.isFavorite}}
							<a role="button" {{on "click" this.removeFromFavoritesAndClose}}>
								<CommonUiIconComponent @name="StarFill" @size="sm" />
								Unfavorite
							</a>
						{{else}}
							<a role="button" {{on "click" this.addToFavoritesAndClose}}>
								<CommonUiIconComponent @name="StarFill" @size="sm" />
								Favorite
							</a>
						{{/if}}
					{{/if}}

					<a role="button" {{on "click" this.openVerificationAndClose}}>
						<CommonUiIconComponent @name="Verified" @size="sm" />
						{{if this.isVerified "Unverify" "Verify"}}
					</a>

					{{#unless this.metric.isPredefinedOnGenericSource}}

						{{#unless this.metric.isTypeGoal}}
							<a role="button" {{on "click" this.createGoalAndClose}}>
								<CommonUiIconComponent @name="Target" @size="sm" />
								Set your goal...
								{{#unless this.goalsEnabled}}
									<BillingLimitationWarningComponent />
								{{/unless}}
							</a>
						{{/unless}}

						{{#if this.queryModel.canEdit}}
							<a role="button" {{on "click" this.editQueryAndClose}}>
								<CommonUiIconComponent @name="Pencil" @size="sm" />
								Edit custom metric
								{{#unless this.qbEnabled}}
									<BillingLimitationWarningComponent />
								{{/unless}}
							</a>
						{{/if}}

						{{#if this.scriptModel.canEdit}}
							<a role="button" {{on "click" this.editScriptAndClose}}>
								<CommonUiIconComponent @name="Pencil" @size="sm" />
								Edit calculated metric
								{{#unless this.scriptsEnabled}}
									<BillingLimitationWarningComponent />
								{{/unless}}
							</a>
						{{/if}}

						<a role="button" {{on "click" this.createNotification}}>
							<CommonUiIconComponent @name="Bell" @size="sm" />
							Notifications
						</a>

						{{#if this.showDuplicateMetric}}
							{{#if this.isDuplicateMetricBlocked}}
								<a role="button" class="disabled">
									<CommonUiIconComponent @name="Copy" @size="sm" />
									Duplicate metric...
									<ToolTipComponent @text={{LEGACY_DATASETS_TOOLTIP}} />
								</a>
							{{else}}
								<a role="button" {{on "click" this.duplicateMetricAndClose}}>
									<CommonUiIconComponent @name="Copy" @size="sm" />
									Duplicate metric...
								</a>
							{{/if}}
						{{/if}}
					{{/unless}}

					{{#if this.metric.allowDimensions}}
						<a role="button" {{on "click" this.viewDimensions}}>
							<CommonUiIconComponent @name="ListBullet" @size="sm" />
							View dimensions
						</a>
					{{/if}}

					{{#if this.canEditMetricDefinition}}
						<a role="button" {{on "click" (fn this.editMetric true)}}>
							<CommonUiIconComponent @name="Settings" @size="sm" />
							Edit metric definition
						</a>
					{{/if}}

					{{#if this.canPushData}}
						<a role="button" {{on "click" this.pushDataAndClose}}>
							<CommonUiIconComponent @name="Upload" @size="sm" />
							Store values
							{{#unless this.dataPushEnabled}}
								<BillingLimitationWarningComponent />
							{{/unless}}
						</a>
					{{/if}}

					{{#if this.canPurgeData}}
						<a role="button" {{on "click" this.purgeDataAndClose}}>
							<CommonUiIconComponent @name="Purge" @size="sm" />
							Purge data
						</a>
					{{/if}}

					{{#if this.canDelete}}
						<a role="button" {{on "click" this.deleteMetricAndClose}} class="red">
							<CommonUiIconComponent @name="Trash" @size="sm" />
							Delete
						</a>
					{{/if}}

				</div>

				{{#unless this.metric.mustResolveBeforeSelecting}}
					<div class="view-insights">
						<a class="button theme-blue" {{on "click" this.openMetricInsightsDrawer}}>
							View metric insights
						</a>
					</div>
				{{/unless}}

			{{/if}}
		{{/if}}
	</template>
}
